import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "lightning-storm";

const DEFAULT_SKY = "#0b1020";
const DEFAULT_BOLT = "#e0f2fe";
const DEFAULT_GLOW = "#7dd3fc";

const STRIKE_INTERVAL_MS = 1900;   // base time between strikes
const STRIKE_JITTER_MS = 1700;     // random extra delay so it never feels metronomic
const FLASH_DURATION_MS = 280;     // how long the screen-wide flash lingers
const BOLT_LIFE_MS = 480;          // how long a fresh bolt stays drawn before fading
const MAX_BOLTS = 4;               // concurrent bolts (a strike can spawn 1–2 forks)

function rand(min, max) { return min + Math.random() * (max - min); }

// Generate one lightning bolt as a polyline that walks downward from `start`,
// with each segment kicking left/right by a random offset and a chance to
// spawn a side-branch (which itself recurses with shorter remaining life).
// `depth` keeps the recursion bounded so we don't spawn a fractal forest.
function buildBolt(startX, startY, endY, depth = 0) {
  const points = [{ x: startX, y: startY }];
  const branches = [];
  const segments = 14 + Math.floor(Math.random() * 10);
  const dy = (endY - startY) / segments;
  let cx = startX;
  let cy = startY;

  for (let i = 0; i < segments; i += 1) {
    cy += dy;
    // Horizontal jitter widens as the bolt descends — gives the
    // characteristic zig-zag spread.
    const jitter = (Math.random() - 0.5) * 38 * (1 - i / segments + 0.2);
    cx += jitter;
    points.push({ x: cx, y: cy });

    // Side-branches: rare near the top, more likely as the bolt descends.
    if (depth < 2 && Math.random() < 0.08 + (i / segments) * 0.06) {
      const branchEndY = cy + rand(40, 140);
      const branchEndX = cx + rand(-90, 90);
      branches.push(buildBolt(cx, cy, Math.min(branchEndY, endY + 20), depth + 1));
      // Force the branch's first point to actually start at the parent
      // junction so they connect cleanly.
      const last = branches[branches.length - 1];
      last.points[0] = { x: cx, y: cy };
      // Bias the branch's endpoint horizontally.
      last.points[last.points.length - 1].x = branchEndX;
    }
  }

  return { points, branches };
}

export default function LightningStorm({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_SKY, DEFAULT_BOLT, DEFAULT_GLOW]);
  const stateRef = useRef({
    bolts: [],         // { bolt, startTs }
    flashes: [],       // { startTs, intensity }
    lastStrikeAt: 0,
    nextStrikeDelay: 0,
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_SKY),
      pickColor(colors, 1, DEFAULT_BOLT),
      pickColor(colors, 2, DEFAULT_GLOW),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const triggerStrike = (ts) => {
      const startX = rand(width * 0.15, width * 0.85);
      const startY = rand(-20, height * 0.1);
      const endY = rand(height * 0.7, height * 1.05);
      const bolt = buildBolt(startX, startY, endY);
      stateRef.current.bolts.push({ bolt, startTs: ts });
      // Some strikes are doubles — adds drama.
      if (Math.random() < 0.35 && stateRef.current.bolts.length < MAX_BOLTS) {
        const startX2 = startX + rand(-120, 120);
        const bolt2 = buildBolt(startX2, startY, endY * rand(0.85, 1.05));
        stateRef.current.bolts.push({ bolt: bolt2, startTs: ts + 80 });
      }
      stateRef.current.flashes.push({
        startTs: ts,
        intensity: rand(0.18, 0.32),
      });
    };

    const drawBolt = (bolt, alpha, boltColor, glowColor) => {
      // Two-pass stroke: wide soft glow underneath, crisp bright core on top.
      // Drawing core after glow keeps the bolt's edges sharp instead of smudgy.
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // Glow pass.
      ctx.strokeStyle = rgba(glowColor, 0.35 * alpha);
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.moveTo(bolt.points[0].x, bolt.points[0].y);
      for (let i = 1; i < bolt.points.length; i += 1) {
        ctx.lineTo(bolt.points[i].x, bolt.points[i].y);
      }
      ctx.stroke();

      // Core pass.
      ctx.strokeStyle = rgba(boltColor, alpha);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(bolt.points[0].x, bolt.points[0].y);
      for (let i = 1; i < bolt.points.length; i += 1) {
        ctx.lineTo(bolt.points[i].x, bolt.points[i].y);
      }
      ctx.stroke();

      // Recurse into branches at lower alpha.
      for (let i = 0; i < bolt.branches.length; i += 1) {
        drawBolt(bolt.branches[i], alpha * 0.65, boltColor, glowColor);
      }
    };

    const tick = (ts) => {
      const [sky, boltColor, glowColor] = colorsRef.current;
      const state = stateRef.current;

      // Sky background — a vertical gradient that gets slightly darker at
      // the bottom, like a thunderhead bearing down.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, sky);
      grad.addColorStop(1, rgba(sky, 0.85));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Schedule next strike.
      if (state.lastStrikeAt === 0) {
        state.lastStrikeAt = ts;
        state.nextStrikeDelay = STRIKE_INTERVAL_MS + Math.random() * STRIKE_JITTER_MS;
      }
      if (
        ts - state.lastStrikeAt > state.nextStrikeDelay &&
        state.bolts.length < MAX_BOLTS
      ) {
        triggerStrike(ts);
        state.lastStrikeAt = ts;
        state.nextStrikeDelay = STRIKE_INTERVAL_MS + Math.random() * STRIKE_JITTER_MS;
      }

      // Screen-wide flash from each active strike. Decays exponentially so
      // the flash burns hot for a moment then fades fast like a real strike.
      let totalFlash = 0;
      for (let i = state.flashes.length - 1; i >= 0; i -= 1) {
        const f = state.flashes[i];
        const age = ts - f.startTs;
        if (age > FLASH_DURATION_MS * 3) {
          state.flashes.splice(i, 1);
          continue;
        }
        const k = Math.exp(-age / (FLASH_DURATION_MS * 0.55));
        totalFlash += f.intensity * k;
      }
      if (totalFlash > 0.005) {
        ctx.fillStyle = rgba(glowColor, Math.min(0.55, totalFlash));
        ctx.fillRect(0, 0, width, height);
      }

      // Bolts.
      for (let i = state.bolts.length - 1; i >= 0; i -= 1) {
        const b = state.bolts[i];
        const age = ts - b.startTs;
        if (age < 0) continue;
        if (age > BOLT_LIFE_MS) {
          state.bolts.splice(i, 1);
          continue;
        }
        // Bolt brightness: pop bright then decay; small flicker mid-life.
        const t = age / BOLT_LIFE_MS;
        const flicker = 0.85 + 0.15 * Math.sin(age * 0.08);
        const alpha = (1 - t) * flicker;
        drawBolt(b.bolt, alpha, boltColor, glowColor);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
